import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { buildMemberDetail } from "../../shared utils/personDetail";

// A printable register for the activity-planner middle column: ONE sheet (page)
// per activity group, listing its members with the leader and activity at the
// top and a tick column for taking the register on paper.

const BRAND_NAVY = [26, 31, 113];

const SLOT_LABELS = {
  DAY: "Daytime",
  AFTERNOON: "Afternoon",
  EVENING: "Evening",
};

function slotLabel(slot) {
  return SLOT_LABELS[String(slot || "").toUpperCase()] || slot || "";
}

function leaderNameOf(group, leaders) {
  if (group?.leaderName) return group.leaderName;
  const match = (leaders || []).find(
    (l) => String(l.staffId) === String(group?.leaderStaffId)
  );
  return match?.name || "Unassigned";
}

function activityNameOf(group, products) {
  if (group?.productName) return group.productName;
  const match = (products || []).find(
    (p) => String(p.productId) === String(group?.productId)
  );
  return match?.productName || "Bespoke";
}

// Members sorted adults-first then by name, matching the on-screen card order.
function sortedMembers(group) {
  const list = Array.isArray(group?.members) ? group.members.slice() : [];
  return list.sort((a, b) => {
    const aAdult = String(a?.personType).toUpperCase() === "ADULT";
    const bAdult = String(b?.personType).toUpperCase() === "ADULT";
    if (aAdult !== bAdult) return aAdult ? -1 : 1;
    return String(a?.name || "").localeCompare(String(b?.name || ""));
  });
}

function memberName(member) {
  const isAdult = String(member?.personType).toUpperCase() === "ADULT";
  const name = String(member?.name ?? "").trim();
  if (name) return isAdult ? `${name} (Adult)` : name;
  return isAdult ? `Adult #${member?.personId}` : `Student #${member?.personId}`;
}

function drawGroupPage(doc, group, { leaders, products, dateStr, campus, slot }) {
  const marginX = 40;
  let y = 48;

  const leader = leaderNameOf(group, leaders);
  const activity = activityNameOf(group, products);
  const members = sortedMembers(group);
  const adults = members.filter(
    (m) => String(m?.personType).toUpperCase() === "ADULT"
  ).length;
  const students = members.length - adults;

  // Title: the group name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BRAND_NAVY);
  doc.text(String(group?.name || "Activity Group"), marginX, y);
  y += 22;

  // Sub-line: campus • date • slot
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(80, 80, 80);
  const when = dateStr ? dayjs(dateStr).format("ddd D MMM YYYY") : "";
  doc.text(
    [campus, when, slotLabel(slot)].filter(Boolean).join("  •  "),
    marginX,
    y
  );
  y += 20;

  // Leader + activity + counts
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Activity: ", marginX, y);
  const actLabelW = doc.getTextWidth("Activity: ");
  doc.setFont("helvetica", "normal");
  doc.text(String(activity), marginX + actLabelW, y);
  y += 16;

  doc.setFont("helvetica", "bold");
  doc.text("Leader: ", marginX, y);
  const ldrLabelW = doc.getTextWidth("Leader: ");
  doc.setFont("helvetica", "normal");
  doc.text(String(leader), marginX + ldrLabelW, y);
  y += 16;

  doc.setTextColor(80, 80, 80);
  doc.setFontSize(10);
  doc.text(
    `${members.length} members — ${adults} adult${adults === 1 ? "" : "s"}, ` +
      `${students} student${students === 1 ? "" : "s"}`,
    marginX,
    y
  );
  y += 10;

  // Register table: tick column, name, detail (booking group / age)
  const body = members.length
    ? members.map((m, i) => [
        String(i + 1),
        "",
        memberName(m),
        buildMemberDetail(m, String(m?.personType).toUpperCase() === "ADULT") || "",
      ])
    : [["", "", "No members assigned", ""]];

  autoTable(doc, {
    startY: y + 8,
    margin: { left: marginX, right: marginX },
    head: [["#", "Present", "Name", "Group / age"]],
    body,
    styles: { fontSize: 11, cellPadding: 5, lineColor: [200, 200, 200], lineWidth: 0.5 },
    headStyles: { fillColor: BRAND_NAVY, textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 28, halign: "center" },
      1: { cellWidth: 60, halign: "center" },
      3: { textColor: [90, 90, 90] },
    },
    // Tall rows so there's room to tick / initial by hand.
    bodyStyles: { minCellHeight: 22 },
  });
}

export function downloadActivityRegistersPdf({
  groups = [],
  leaders = [],
  products = [],
  dateStr,
  campus,
  slot,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  if (!groups.length) {
    doc.setFontSize(14);
    doc.text("No activity groups for this selection.", 40, 60);
  } else {
    groups.forEach((group, index) => {
      if (index > 0) doc.addPage();
      drawGroupPage(doc, group, { leaders, products, dateStr, campus, slot });
    });
  }

  const safeCampus = String(campus || "campus").replace(/[^\w-]+/g, "_");
  const datePart = dateStr ? dayjs(dateStr).format("YYYY-MM-DD") : "";
  doc.save(`Activity_Registers_${safeCampus}_${datePart}_${slotLabel(slot)}.pdf`);
}
